import { Routes } from "@angular/router";
import { AuthGuard, NonAuthGuard } from "./guards/auth.guard";
import { UserCognitoResolver, UserResolver } from "./resolvers/user.resolver";
import { OrderResolver, StoreDraftOrderResolver, StoreOrderResolver } from "./resolvers/order.resolver";
import { ProductResolver } from "./resolvers/product.resolver";
import { StoreResolver } from "./resolvers/store.resolver";
import { VendorResolver } from "./resolvers/vendor.resolver";

export const routes: Routes = [
  {
    path: "home",
    loadComponent: () => import("./home/home.page").then((m) => m.HomePage),
    canActivate: [AuthGuard],
    resolve: {
      user: UserResolver,
    },
  },
  {
    path: "",
    redirectTo: "home",
    pathMatch: "full",
  },
  {
    path: "login",
    loadComponent: () =>
      import("./auth/login/login.page").then((m) => m.LoginPage),
    canActivate: [NonAuthGuard],
  },
  {
    path: "set-new-password",
    loadComponent: () =>
      import("./auth/set-new-password/set-new-password.page").then(
        (m) => m.SetNewPasswordPage,
      ),
    canActivate: [NonAuthGuard],
  },
  {
    path: "register",
    loadComponent: () =>
      import("./auth/register/register.page").then((m) => m.RegisterPage),
    canActivate: [NonAuthGuard],
  },

  {
    path: "stores",
    canActivate: [AuthGuard],
    children: [
      {
        path: "",
        redirectTo: "",
        pathMatch: "full",
      },
      {
        path: "",
        loadComponent: () =>
          import("./pages/stores/stores.page").then((m) => m.StoresPage),
      },
      {
        path: "import",
        loadComponent: () =>
          import("./pages/stores/import-store/import-store.page").then(
            (m) => m.ImportStorePage,
          ),
      },
      {
        path: "create",
        loadComponent: () =>
          import("./pages/stores/create-store/create-store.page").then(
            (m) => m.CreateStorePage,
          ),
      },
      {
        path: ":id/edit",
        loadComponent: () =>
          import("./pages/stores/edit-store/edit-store.page").then(
            (m) => m.EditStorePage,
          ),
        resolve: {
          store: StoreResolver,
        },
      },
      {
        path: "**",
        redirectTo: "",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "store-types",
    loadComponent: () =>
      import("./pages/store-types/store-types.page").then(
        (m) => m.StoreTypesPage,
      ),
  },
  {
    path: "store-channels",
    loadComponent: () =>
      import("./pages/store-channels/store-channels.page").then(
        (m) => m.StoreChannelsPage,
      ),
  },
  {
    path: "store-sub-channels",
    loadComponent: () =>
      import("./pages/store-sub-channels/store-sub-channels.page").then(
        (m) => m.StoreSubChannelsPage,
      ),
  },
  {
    path: "vendors",
    canActivate: [AuthGuard],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./pages/vendor/vendor-list.page").then(
            (m) => m.VendorListPage,
          ),
      },
      {
        path: "create",
        loadComponent: () => import('./pages/vendor/vendor-create.page').then(m => m.VendorCreatePage),
      },
      {
        path: ":vendorId/edit",
        loadComponent: () =>
          import("./pages/vendor/vendor-create.page").then((m) => m.VendorCreatePage),
      },
      {
        path: ":vendorId",
        loadComponent: () =>
          import("./pages/vendor/vendor-detail.page").then(
            (m) => m.VendorDetailPage,
          ),
        resolve: {
          vendor: VendorResolver,
        },
      },
      {
        path: "**",
        redirectTo: "",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "clients",
    loadComponent: () =>
      import("./pages/clients/clients.page").then((m) => m.ClientsPage),
    canActivate: [AuthGuard],
  },
  {
    path: "projects",
    loadComponent: () =>
      import("./pages/projects/projects.page").then((m) => m.ProjectsPage),
  },
  {
    path: "products",
    children: [
      {
        path: "",
        redirectTo: "",
        pathMatch: "full",
      },
      {
        path: "",
        loadComponent: () =>
          import("./pages/products/products.page").then((m) => m.ProductsPage),
      },
      {
        path: "create",
        loadComponent: () =>
          import("./pages/products/create-product/create-product.page").then(
            (m) => m.CreateProductPage,
          ),
      },
      {
        path: ":id/edit",
        loadComponent: () =>
          import("./pages/products/edit-product/edit-product.page").then(
            (m) => m.EditProductPage,
          ),
        resolve: {
          product: ProductResolver,
        },
      },
      {
        path: "import",
        loadComponent: () =>
          import("./pages/products/import-product/import-product.page").then(
            (m) => m.ImportProductPage,
          ),
      },
      {
        path: "**",
        redirectTo: "",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "order-types",
    loadComponent: () =>
      import("./pages/order-types/order-types.page").then(
        (m) => m.OrderTypesPage,
      ),
  },
  {
    path: "orders",
    canActivate: [AuthGuard],
    children: [
      {
        path: "",
        redirectTo: "",
        pathMatch: "full",
      },
      {
        path: "",
        loadComponent: () =>
          import("./pages/orders/orders.page").then((m) => m.OrdersPage),
      },
      {
        path: "view",
        children: [
          {
            path: ":referenceNumber",
            loadComponent: () =>
              import("./pages/orders/order-detail/order-detail.page").then(
                (m) => m.OrderDetailPage,
              ),
            resolve: {
              order: OrderResolver,
            },
          },
          {
            path: ":referenceNumber/:action/:storeId",
            loadComponent: () =>
              import(
                "./pages/orders/order-detail/order-activity-view/order-activity-view.page"
              ).then((m) => m.OrderActivityViewPage),
            resolve: {
              store: StoreDraftOrderResolver,
            },
          },
        ],
      },
      {
        path: "import",
        loadComponent: () =>
          import("./pages/orders/import-order/import-order.page").then(
            (m) => m.ImportOrderPage,
          ),
      },
      {
        path: "create",
        loadComponent: () =>
          import("./pages/orders/create-order/create-order.page").then(
            (m) => m.CreateOrderPage,
          ),
      },
      {
        path: "market-stalls",
        loadComponent: () =>
          import("./pages/orders/market-stalls/market-stalls.page").then(
            (m) => m.MarketStallsPage,
          ),
      },
      {
        path: "market-stalls/import",
        loadComponent: () =>
          import("./pages/orders/market-stalls/import-market-stalls/import-market-stalls.page").then(
            (m) => m.ImportMarketStallsPage,
          ),
      },
      {
        path: "**",
        redirectTo: "",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "groups",
    loadComponent: () =>
      import("./pages/groups/groups.page").then((m) => m.GroupsPage),
  },
  {
    path: "public-markets",
    loadComponent: () =>
      import("./pages/orders/public-markets/public-markets.page").then(
        (m) => m.PublicMarketsPage,
      ),
  },
  {
    path: "users",
    children: [
      {
        path: "",
        redirectTo: "",
        pathMatch: "full",
      },
      {
        path: "",
        loadComponent: () =>
          import("./pages/users/users.page").then((m) => m.UsersPage),
      },
      {
        path: "create",
        loadComponent: () =>
          import("./pages/users/create-user/create-user.page").then(
            (m) => m.CreateUserPage,
          ),
      },
      {
        path: ":sub/edit",
        loadComponent: () =>
          import("./pages/users/edit-user/edit-user.page").then(
            (m) => m.EditUserPage,
          ),
        resolve: {
          user: UserCognitoResolver,
        },
      },
      {
        path: "**",
        redirectTo: "",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "posm",
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./pages/posm/posm-list/posm-list.page").then(
            (m) => m.POSMListPage,
          ),
      },
      {
        path: ":id",
        loadComponent: () =>
          import("./pages/posm/posm-detail/posm-detail.page").then(
            (m) => m.POSMDetailPage,
          ),
      },
    ],
  },
];
